import { useState, useCallback } from 'react';

export type Logical = 'AND' | 'OR';

export type TextOperator =
  | 'contains'
  | 'does_not_contain'
  | 'equals'
  | 'not_equals'
  | 'starts_with'
  | 'ends_with'
  | 'is_empty'
  | 'is_not_empty'
  | 'is_one_of'
  | 'is_not_one_of';

export type NumberOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_empty'
  | 'is_not_empty';

export type DateOperator =
  | 'on'
  | 'before'
  | 'after'
  | 'between'
  | 'in_the_last'
  | 'not_in_the_last'
  | 'is_empty'
  | 'is_not_empty';

export type BooleanOperator =
  | 'is_true'
  | 'is_false'
  | 'is_empty'
  | 'is_not_empty';

export type Operator = TextOperator | NumberOperator | DateOperator | BooleanOperator;

export interface Condition {
  id: string;
  fieldId: string;
  operator: Operator;
  value?: any;
  error?: string;
  combiner?: Logical;
}

export interface FiltersState {
  conditions: Condition[];
}

export const useMultiFilter = () => {
  const [state, setState] = useState<FiltersState>({
    conditions: []
  });

  const addCondition = useCallback((fieldId?: string, operator?: Operator) => {
    const newCondition: Condition = {
      id: `condition_${Date.now()}_${Math.random()}`,
      fieldId: fieldId || '',
      operator: operator || 'contains',
      value: undefined,
      combiner: state.conditions.length > 0 ? 'AND' : undefined
    };

    setState(prev => ({
      conditions: [...prev.conditions, newCondition]
    }));
  }, [state.conditions.length]);

  const removeCondition = useCallback((index: number) => {
    setState(prev => {
      const newConditions = prev.conditions.filter((_, i) => i !== index);

      // Remove combiner from first condition if it exists
      if (newConditions.length > 0 && newConditions[0].combiner) {
        newConditions[0] = { ...newConditions[0], combiner: undefined };
      }

      return { conditions: newConditions };
    });
  }, []);

  const updateCondition = useCallback((index: number, updates: Partial<Condition>) => {
    setState(prev => ({
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, ...updates } : condition
      )
    }));
  }, []);

  const reorderConditions = useCallback((fromIndex: number, toIndex: number) => {
    setState(prev => {
      const newConditions = [...prev.conditions];
      const [removed] = newConditions.splice(fromIndex, 1);
      newConditions.splice(toIndex, 0, removed);

      // Update combiners: first condition should have no combiner
      newConditions[0] = { ...newConditions[0], combiner: undefined };
      for (let i = 1; i < newConditions.length; i++) {
        if (!newConditions[i].combiner) {
          newConditions[i] = { ...newConditions[i], combiner: 'AND' };
        }
      }

      return { conditions: newConditions };
    });
  }, []);

  const clear = useCallback(() => {
    setState({ conditions: [] });
  }, []);

  const updateState = useCallback((updates: Partial<FiltersState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    state,
    addCondition,
    removeCondition,
    updateCondition,
    reorderConditions,
    clear,
    updateState
  };
};
